import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  Box,
  CircularProgress,
  Typography,
  Fade,
  Paper,
  LinearProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';

const ContentContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  maxWidth: 400,
  width: '90%',
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  boxShadow: theme.shadows[10],
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'inline-flex',
  marginBottom: theme.spacing(3),
}));

// Komponen utama overlay dengan PORTAL
const OverlayLazyLoadCircularProgressLabel = ({
  open,
  title = 'Loading Application',
  subtitle = 'Please wait while we prepare your workspace',
  currentStep = 1,
  totalSteps = 4,
  progressValue,
  loadingSteps = [
    { step: 1, description: 'Checking authentication...' },
    { step: 2, description: 'Loading user data...' },
    { step: 3, description: 'Fetching user role...' },
    { step: 4, description: 'Preparing workspace...' },
  ],
  additionalText,
  showSteps = true,
  showProgressBar = false,
  transparentBackground = false,
}) => {
  const portalRef = useRef(null);
  const overlayRef = useRef(null);

  // Setup portal
  useEffect(() => {
    // Buat div untuk portal
    const portalDiv = document.createElement('div');
    portalDiv.id = 'overlay-portal-root';
    portalDiv.style.position = 'fixed';
    portalDiv.style.top = '0';
    portalDiv.style.left = '0';
    portalDiv.style.width = '100%';
    portalDiv.style.height = '100%';
    portalDiv.style.zIndex = '9999';
    portalDiv.style.pointerEvents = open ? 'auto' : 'none'; // PENTING: non-aktif saat tidak open
    
    if (!document.getElementById('overlay-portal-root')) {
      document.body.appendChild(portalDiv);
    }
    
    portalRef.current = portalDiv;

    return () => {
      // Hapus portal saat komponen unmount
      if (portalRef.current && portalRef.current.parentNode) {
        portalRef.current.parentNode.removeChild(portalRef.current);
      }
    };
  }, []);

  // Update pointer-events saat open berubah
  useEffect(() => {
    if (portalRef.current) {
      portalRef.current.style.pointerEvents = open ? 'auto' : 'none';
      
      // Juga update ref overlay jika ada
      if (overlayRef.current) {
        overlayRef.current.style.pointerEvents = open ? 'auto' : 'none';
      }
    }
  }, [open]);

  if (!open) {
    // Saat tidak open, return null dan pastikan portal dihapus
    if (portalRef.current && portalRef.current.parentNode) {
      setTimeout(() => {
        if (portalRef.current && portalRef.current.parentNode) {
          portalRef.current.parentNode.removeChild(portalRef.current);
        }
      }, 500);
    }
    return null;
  }

  const currentStepData = loadingSteps.find(step => step.step === currentStep);
  const stepLabel = currentStepData?.description || 'Loading...';
  
  const calculatedProgress = progressValue !== undefined 
    ? progressValue 
    : ((currentStep - 1) / totalSteps) * 100;

  // Konten overlay
  const overlayContent = (
    <Fade in={open} timeout={300}>
      <Box
        ref={overlayRef}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: transparentBackground 
            ? 'rgba(255, 255, 255, 0.7)' 
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: transparentBackground ? 'blur(2px)' : 'none',
          pointerEvents: 'auto', // Aktif saat open
        }}
      >
        <ContentContainer>
          <CircularProgressWithLabel
            value={calculatedProgress}
            label={stepLabel}
          />
          
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: 'text.primary',
              textAlign: 'center',
            }}
          >
            {title}
          </Typography>
          
          <Typography
            variant="body2"
            sx={{
              mb: 3,
              color: 'text.secondary',
              textAlign: 'center',
            }}
          >
            {subtitle}
          </Typography>
          
          {showSteps && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                width: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                borderRadius: 2,
              }}
            >
              {loadingSteps.map((step) => (
                <LoadingStep
                  key={step.step}
                  step={step.step}
                  description={step.description}
                  currentStep={currentStep}
                />
              ))}
            </Paper>
          )}
          
          {showProgressBar && (
            <Box sx={{ width: '100%', mb: 3 }}>
              <LinearProgress
                variant="determinate"
                value={calculatedProgress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  textAlign: 'center',
                  mt: 1,
                  color: 'text.secondary',
                }}
              >
                {Math.round(calculatedProgress)}% complete
              </Typography>
            </Box>
          )}
          
          {additionalText && (
            <Typography
              variant="caption"
              sx={{
                mt: 2,
                color: 'text.secondary',
                textAlign: 'center',
                fontStyle: 'italic',
              }}
            >
              {additionalText}
            </Typography>
          )}
          
          <Typography
            variant="body2"
            sx={{
              mt: 3,
              color: 'primary.main',
              fontWeight: 'medium',
              textAlign: 'center',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.7 },
              },
            }}
          >
            Load Data ({currentStepData?.description || 'Processing'})
          </Typography>
        </ContentContainer>
      </Box>
    </Fade>
  );

  // Render ke portal jika portal sudah ada
  if (!portalRef.current) {
    return null;
  }

  return ReactDOM.createPortal(overlayContent, portalRef.current);
};

// Komponen sub (tetap sama)
const CircularProgressWithLabel = ({ value, label }) => {
  return (
    <ProgressContainer>
      <CircularProgress
        variant={value ? "determinate" : "indeterminate"}
        value={value}
        size={80}
        thickness={4}
        sx={{
          color: (theme) => theme.palette.primary.main,
        }}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant="caption"
          component="div"
          color="text.secondary"
          sx={{ fontWeight: 'bold' }}
        >
          {value ? `${Math.round(value)}%` : label}
        </Typography>
      </Box>
    </ProgressContainer>
  );
};

const LoadingStep = ({ step, description, currentStep }) => {
  const isActive = step === currentStep;
  const isCompleted = step < currentStep;
  
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        mb: 1,
        opacity: isActive ? 1 : isCompleted ? 0.7 : 0.4,
        transition: 'opacity 0.3s ease',
      }}
    >
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          backgroundColor: isCompleted
            ? (theme) => theme.palette.success.main
            : isActive
            ? (theme) => theme.palette.primary.main
            : (theme) => theme.palette.grey[400],
          mr: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '0.75rem',
          fontWeight: 'bold',
        }}
      >
        {isCompleted ? '✓' : step}
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontWeight: isActive ? 'bold' : 'normal',
          color: isActive ? 'text.primary' : 'text.secondary',
        }}
      >
        {description}
      </Typography>
    </Box>
  );
};

export default OverlayLazyLoadCircularProgressLabel;